import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowsFromLine,
  faArrowsToLine,
  faClock,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faPlaneDeparture,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React from 'react';
import { IconButton } from '../ui/nucleus/Button/IconButton';

export type PlannerToolbarProps = {
  /** Whether to show times on job pills */
  showTimes: boolean;
  /** Toggle show times */
  onToggleShowTimes: () => void;
  /** Whether to show bookings */
  showBookings: boolean;
  /** Toggle show bookings */
  onToggleShowBookings: () => void;
  /** Whether any sites are expanded */
  hasExpandedSites: boolean;
  /** Toggle expand/collapse all sites */
  onToggleExpandAll: () => void;
  /** Zoom in handler */
  onZoomIn?: () => void;
  /** Zoom out handler */
  onZoomOut?: () => void;
  /** Whether zoom in is available */
  canZoomIn?: boolean;
  /** Whether zoom out is available */
  canZoomOut?: boolean;
  /** Add job handler */
  onAddJob: () => void;
  /** Whether add job form is loading */
  isAddJobLoading: boolean;
};

/**
 * PlannerToolbar - Memoized toolbar component for the planner view.
 *
 * Extracted from PlannerView to improve mounting/unmounting performance.
 * Uses React.memo to prevent re-renders when parent state changes but
 * toolbar props remain the same.
 */
export const PlannerToolbar = React.memo(function PlannerToolbar({
  showTimes,
  onToggleShowTimes,
  showBookings,
  onToggleShowBookings,
  hasExpandedSites,
  onToggleExpandAll,
  onZoomIn,
  onZoomOut,
  canZoomIn,
  canZoomOut,
  onAddJob,
  isAddJobLoading,
}: PlannerToolbarProps) {
  return (
    <div
      className={classNames(
        'fixed z-30 flex flex-col',
        // Mobile placement just above typical FAB
        'bottom-28 left-[10px] right-0',
        // Desktop/tablet: center over main content area
        'md:absolute md:bottom-[4.2rem] md:left-0 md:right-0 md:z-50 md:flex-row md:pt-0 lg:bottom-8 lg:pl-[170px]'
      )}
    >
      <div
        className='z-30 mx-auto flex h-fit items-center gap-1 rounded-full bg-white/95 p-1 text-secondary shadow-md ring-1 ring-outline-variant md:relative md:bottom-auto md:left-auto md:right-auto md:flex md:flex-row md:px-2 md:py-2'
        role='toolbar'
        aria-label='Calendar view options'
        data-usetiful='planner-view-toolbar'
      >
        {/* Show/Hide Times Button */}
        <IconButton
          ariaLabel={showTimes ? 'Hide times' : 'Show times'}
          onClick={onToggleShowTimes}
          toggled={showTimes}
          tooltip={showTimes ? 'Hide times' : 'Show times'}
          size='md'
          variant={showTimes ? 'outlined' : 'default'}
          icon={<FontAwesomeIcon icon={faClock as IconProp} />}
        />

        {/* Show/Hide Bookings Button */}
        <IconButton
          ariaLabel={showBookings ? 'Hide bookings' : 'Show bookings'}
          onClick={onToggleShowBookings}
          toggled={showBookings}
          tooltip={showBookings ? 'Hide bookings' : 'Show bookings'}
          size='md'
          variant={showBookings ? 'outlined' : 'default'}
          icon={<FontAwesomeIcon icon={faPlaneDeparture as IconProp} />}
        />

        <ToolbarDivider />

        {/* Expand/Collapse All Button */}
        <IconButton
          ariaLabel={hasExpandedSites ? 'Collapse all' : 'Expand all'}
          onClick={onToggleExpandAll}
          tooltip={hasExpandedSites ? 'Collapse all rows' : 'Expand all rows'}
          size='md'
          variant='default'
          icon={
            hasExpandedSites ? (
              <FontAwesomeIcon icon={faArrowsToLine as IconProp} />
            ) : (
              <FontAwesomeIcon icon={faArrowsFromLine as IconProp} />
            )
          }
        />

        <ToolbarDivider />

        {/* Zoom In Button */}
        <IconButton
          ariaLabel='Zoom in'
          onClick={() => onZoomIn?.()}
          disabled={canZoomIn === false}
          tooltip='Zoom in'
          size='md'
          variant='default'
          icon={<FontAwesomeIcon icon={faMagnifyingGlassPlus as IconProp} />}
        />

        {/* Zoom Out Button */}
        <IconButton
          ariaLabel='Zoom out'
          onClick={() => onZoomOut?.()}
          disabled={canZoomOut === false}
          tooltip='Zoom out'
          size='md'
          variant='default'
          icon={<FontAwesomeIcon icon={faMagnifyingGlassMinus as IconProp} />}
        />

        {/* Mobile-only Add Job Button */}
        <ToolbarDivider className='lg:hidden' />
        <div className='lg:hidden'>
          <IconButton
            ariaLabel='Add job'
            tooltip='Add job'
            onClick={onAddJob}
            disabled={isAddJobLoading}
            size='md'
            variant='primary'
            icon={
              isAddJobLoading ? (
                <LoadingSpinner />
              ) : (
                <FontAwesomeIcon icon={faPlus as IconProp} />
              )
            }
          />
        </div>
      </div>
    </div>
  );
});

/**
 * Toolbar divider component for visual separation between button groups
 */
const ToolbarDivider = ({ className }: { className?: string }) => (
  <div
    className={classNames('mx-1 block h-5 w-px bg-outline-variant', className)}
  />
);

/**
 * Loading spinner for the add job button
 */
const LoadingSpinner = () => (
  <svg
    className='h-6 w-6 animate-spin'
    xmlns='http://www.w3.org/2000/svg'
    fill='none'
    viewBox='0 0 24 24'
    aria-hidden='true'
  >
    <circle
      className='opacity-25'
      cx='12'
      cy='12'
      r='10'
      stroke='currentColor'
      strokeWidth='4'
    />
    <path
      className='opacity-75'
      fill='currentColor'
      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
    />
  </svg>
);
